import {
  IconType,
  Heading,
  Button,
  Icon,
  InputRadioGroup,
  Popover,
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { NAMESPACE } from 'locale'
import { selfServeConstants } from 'locale/selfServe'
import { Text } from '@lmc/ui-kit/src/general'
import Operations from 'operations'

import { FC, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Home } from '@lmc/ts-common'
import { LogoutButton } from './LogoutButton'

export type Props = { next: () => void }

export const StepPreferences: FC<Props> = () => {
  const { t } = useTranslation(NAMESPACE.SELF_SERVE)

  const { t: tGlobal } = useTranslation()

  const dispatch = useDispatch()

  const home: Home | undefined = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const updatePreferences = () => {
    dispatch(
      Operations.saveHomeDetails({
        id: home?.id,
        preferredPeopleNominative,
        preferredCarePlansNominative,
        preferencesCustomised: true,
      })
    )
  }

  const [preferredCarePlansNominative, setPreferredCarePlansNominative] =
    useState<string>('')

  const [preferredPeopleNominative, setPreferredPeopleNominative] =
    useState<string>('')

  const canContinue =
    preferredCarePlansNominative !== '' && preferredPeopleNominative !== ''

  const peopleOptions = [
    {
      label: tGlobal('general.options.peopleOptions.clients'),
      value: 'Clients',
    },
    {
      label: tGlobal('general.options.peopleOptions.customers'),
      value: 'Customers',
    },
    {
      label: tGlobal('general.options.peopleOptions.serviceUsers'),
      value: 'Service Users',
    },
    {
      label: tGlobal('general.options.peopleOptions.residents'),
      value: 'Residents',
    },
    {
      label: tGlobal('general.options.peopleOptions.supportedPersons'),
      value: 'Supported Persons',
    },
    {
      label: tGlobal('general.options.peopleOptions.tenants'),
      value: 'Tenants',
    },
  ]

  const planOptions = [
    {
      label: tGlobal('general.options.planOptions.carePlans'),
      value: 'Care Plans',
    },
    {
      label: tGlobal('general.options.planOptions.supportPlans'),
      value: 'Support Plans',
    },
  ]

  return (
    <>
      <Icon
        customIcon={IconType.CleanLargeLogoIcon}
        style={{
          height: '100vh',
          width: 1000,
          position: 'absolute',
          right: 0,
          bottom: 0,
          top: '-20px',
          marginRight: 0,
        }}
      />
      <Wrapper>
        <TopWrapper>
          <Icon
            customIcon={IconType.LargeLogoIcon}
            style={{ height: 100, width: 250 }}
          />
          <LogoutButton />
        </TopWrapper>
        <CenterWrapper>
          <StyledHeading level={1}>
            {t(selfServeConstants.SET_YOUR_PREFFERED)}
          </StyledHeading>
          <StyledText>{t(selfServeConstants.BY_CHANGING)}</StyledText>
          <MaxWidth>
            <InputRadioGroupStyled
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onChange={(e) => {
                setPreferredPeopleNominative(e)
              }}
              value={preferredPeopleNominative}
              label={tGlobal('general.options.peopleOptions.label')}
              options={peopleOptions}
            />
            <InputRadioGroupStyled
              onChange={(e) => setPreferredCarePlansNominative(e)}
              value={preferredCarePlansNominative}
              label={tGlobal('general.options.planOptions.label')}
              options={planOptions}
            />
          </MaxWidth>
          <ButtonWrapper>
            {canContinue ? (
              <StyledButton
                variant="primary"
                size="large"
                onClick={updatePreferences}
              >
                {t(selfServeConstants.NEXT)}
              </StyledButton>
            ) : (
              <Popover content={t(selfServeConstants.MAKE_SELECTION)}>
                <StyledDisabledButton variant="primary" size="large">
                  {t(selfServeConstants.NEXT)}
                </StyledDisabledButton>
              </Popover>
            )}
            <StyledTextBottom>
              {t(selfServeConstants.STEP_X_OF_X, { from: 1, to: 3 })}
            </StyledTextBottom>
          </ButtonWrapper>
        </CenterWrapper>
      </Wrapper>
    </>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.color.background.primary};
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 100px 160px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 10px;
  }
`

const CenterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: auto;
  margin-top: auto;
`

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 30px;
`

const MaxWidth = styled.div`
  width: 600;
`

const StyledHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color11};
  font-weight: 800;
  font-size: 54px;
  line-height: 59px;
  max-width: 800px;
  font-family: gilroy;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color11};
  font-size: 24px;
  font-weight: 400;
  line-height: 32px;
  opacity: 0.7;
  max-width: 800px;
  margin-top: 10px;
  margin-bottom: 20px;
`

const StyledTextBottom = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color11};
  margin-left: 20px;
  margin-top: 5px;
`

const StyledButton = styled(Button)`
  width: max-content;
  z-index: 10;
`

const StyledDisabledButton = styled(Button)`
  width: max-content;
  opacity: 0.5;
`

const InputRadioGroupStyled = styled(InputRadioGroup)`
  .ant-radio-group {
    flex-direction: row;
    white-space: no-wrap;
  }
  .ant-radio-wrapper > span {
    font-size: 16px !important;
  }

  .ant-radio-wrapper {
    border-radius: 9px;
    border-width: 0px !important;
  }

  .ant_not_full_width {
    min-width: unset;
  }

  .ant-form-item-label > label {
    color: ${({ theme }) => theme.color.neutral.color11} !important;
    font-size: 20px;
    font-weight: 700;
    margin-top: 15px;
    margin-bottom: 15px;
  }
`

const TopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`
